import { useMemo } from 'react';
import { PRODUCT_DISCOVERY_PROJECT } from '@jira/platform__project-types/src/project-types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
// eslint-disable-next-line jira/restricted/@jira+platform__services-providers-project-context
import { useProjectContext } from '@jira/platform__services-providers-project-context/src/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useEffectWithoutConcurrency } from '@atlassian/jira-react-strict-mode/src/useEffectWithoutConcurrency.tsx';
import { BANNER_NAME } from './bannerName.constants.tsx';
import {
	useJpdBlueprintsFeedbackBannerActions,
	useJpdBlueprintsFeedbackBannerState,
} from './useJpdBlueprintsFeedbackBannerActions.tsx';
import { useHasPolarisInPath } from './useHasPolarisInPath.tsx';

export const useBannerJpdBlueprintsFeedback = (): {
	isEnabled: boolean;
	name: string;
	height: number;
} => {
	const hasPolarisInPath = useHasPolarisInPath();
	const { data: project } = useProjectContext();
	const accountId = useAccountId();
	const { isDismissed, height, shouldShow, isLoading } = useJpdBlueprintsFeedbackBannerState();
	const { fetchBannerVisibility } = useJpdBlueprintsFeedbackBannerActions();
	const isDiscoveryProject = project?.projectType === PRODUCT_DISCOVERY_PROJECT;

	const isInJpdBoundary = useMemo(
		() => hasPolarisInPath || isDiscoveryProject,
		[isDiscoveryProject, hasPolarisInPath],
	);

	useEffectWithoutConcurrency(() => {
		if (!fg('jpd-view-templates-feedback-banner')) {
			return;
		}

		if (!accountId) {
			return;
		}

		// Fetch only if we are in JPD boundary
		if (isInJpdBoundary) {
			fetchBannerVisibility(accountId, project?.projectId);
		}
	}, [accountId, fetchBannerVisibility, project, isInJpdBoundary]);

	return useMemo(
		() => ({
			isEnabled:
				isInJpdBoundary &&
				shouldShow &&
				!isDismissed &&
				!isLoading &&
				fg('jpd-view-templates-feedback-banner'),
			name: BANNER_NAME,
			height,
		}),
		[isInJpdBoundary, shouldShow, isDismissed, height, isLoading],
	);
};
